<script setup lang="ts">
import { ref } from 'vue';
import { useMapRenderer } from '@/composables/useMapRenderer';
import { useMapStore } from '@/stores/map';
import { useAssetsStore } from '@/stores/assets';

const mapStore = useMapStore();
const assetsStore = useAssetsStore();

const containerRef = ref<HTMLElement | null>(null);
const { isInitialized } = useMapRenderer(containerRef);
</script>

<template>
    <div class="map-viewport" ref="containerRef">
        <!-- Loading overlay - only show during initial atlas load, not incremental sprite loading -->
        <div class="loading-overlay" v-if="assetsStore.loading && !assetsStore.atlas">
            <div class="loading-content">
                <div class="loading-spinner"></div>
                <p>Loading atlas...</p>
            </div>
        </div>

        <!-- Sprite loading indicator (less intrusive) -->
        <div class="sprite-loading-indicator" v-if="assetsStore.loading && assetsStore.atlas">
            <div class="loading-spinner-small"></div>
            <span>Loading sprites... {{ Math.round(assetsStore.loadProgress * 100) }}%</span>
        </div>

        <!-- Error overlay -->
        <div class="error-overlay" v-if="assetsStore.error">
            <div class="error-content">
                <p class="error-title">Failed to load assets</p>
                <p class="error-message">{{ assetsStore.error }}</p>
                <p class="error-hint">
                    Make sure to run the PAK converter first:<br>
                    <code>cd tools/pak-converter && npm run convert</code>
                </p>
            </div>
        </div>

        <!-- No map overlay -->
        <div class="no-map-overlay" v-if="!mapStore.hasMap && isInitialized && !assetsStore.loading">
            <div class="no-map-content">
                <p>No map loaded</p>
                <p class="hint">Press Ctrl+N to create a new map or Ctrl+O to open an existing one</p>
            </div>
        </div>
    </div>
</template>

<style scoped>
.map-viewport {
    width: 100%;
    height: 100%;
    background: var(--bg-primary);
    position: relative;
    overflow: hidden;
}

.map-viewport canvas {
    display: block;
}

.loading-overlay,
.error-overlay,
.no-map-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(26, 26, 46, 0.9);
    z-index: 10;
}

.loading-content,
.error-content,
.no-map-content {
    text-align: center;
    color: var(--text-secondary);
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--border-color);
    border-top-color: var(--accent-color);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 16px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.error-title {
    font-size: 18px;
    color: var(--error-color);
    margin-bottom: 8px;
}

.error-message {
    margin-bottom: 16px;
}

.error-hint {
    font-size: 13px;
    line-height: 1.6;
}

.error-hint code {
    display: inline-block;
    background: var(--bg-secondary);
    padding: 4px 8px;
    border-radius: 4px;
    margin-top: 8px;
    font-family: monospace;
}

.no-map-content .hint {
    font-size: 13px;
    margin-top: 8px;
}

.sprite-loading-indicator {
    position: absolute;
    bottom: 16px;
    left: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(26, 26, 46, 0.9);
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 12px;
    color: var(--text-secondary);
    z-index: 5;
}

.loading-spinner-small {
    width: 16px;
    height: 16px;
    border: 2px solid var(--border-color);
    border-top-color: var(--accent-color);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}
</style>
